
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { of, Subscription } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import CreateCustomerUseCase from '../../../../Domain/Customers/CreateCustomerUseCase';
import Customer from '../../../../Domain/Entities/Customer';
import { Constants } from '../../../../Utils/Constants';
import KeyValuePair from '../../../../Utils/KeyValuePair';
import { Failure, Loading, Success } from '../../../../Utils/Result';
import NewCustomerInfo from './NewCustomerInfo';
import NewCustomerView from './NewCustomerView';
import Logger from '../../../../Logger/Logger';
import strings from '../../../Utils/LocalizedStrings';

export default class NewCustomerPresenter {

  private readonly subscription = new Subscription();

  private info?: NewCustomerInfo;
  private view?: NewCustomerView;

  constructor(
    private readonly createCustomerUseCase: CreateCustomerUseCase,
  ) {
  }

  attachView = (v: NewCustomerView) => {
    this.view = v;
  }

  detachView = () => {
    this.view = undefined;
    this.subscription.unsubscribe();
  }

  setCustomerInfo = (info?: NewCustomerInfo) => {
    this.info = info;
  }

  saveCustomer = () => {
    const info = this.info;
    if (!info) return;

    let areInfoValid = true;

    if (info.name.length === 0) {
      this.view?.showNameError(strings.admin.clients.creation.errorMissingName);
      areInfoValid = false;
    } else {
      this.view?.showNameError("");
    }

    if (info.surname.length === 0) {
      this.view?.showSurnameError(strings.admin.clients.creation.errorMissingSurname);
      areInfoValid = false;
    } else {
      this.view?.showSurnameError("");
    }

    const email = info.email;
    if (email.length >= 0 && !email.match(Constants.emailRegex)) {
      this.view?.showEmailError(strings.admin.clients.creation.errorInvalidEmail);
      areInfoValid = false;
    } else {
      this.view?.showEmailError("");
    }

    if (info.phoneNumber && info.phoneNumber.length > 0 && !info.phoneNumber.match(Constants.phoneNumberRegex)) {
      this.view?.showPhoneError(strings.admin.clients.creation.errorInvalidPhone);
      areInfoValid = false;
    } else if (info.phoneNumber && !(parsePhoneNumberFromString(info.phoneNumber)?.country)) {
      this.view?.showPhoneError(strings.admin.clients.creation.errorMissingPhonePrefix);
      areInfoValid = false;
    } else {
      this.view?.showPhoneError("");
    }

    if (areInfoValid) {
      const entries: KeyValuePair<string, string>[] = [
        { key: strings.admin.clients.detail.name, value: info.name },
        { key: strings.admin.clients.detail.surname, value: info.surname },
        { key: strings.admin.clients.detail.email, value: info.email }
      ];
      if (info.phoneNumber) {
        entries.push({ key: strings.admin.clients.detail.phone, value: info.phoneNumber });
      }
      this.view?.showConfirmationDialog(entries);
    }
  }

  saveCustomerConfirmed = () => {
    if (!this.info) return;

    this.subscription.add(
      this.createCustomerUseCase
        .execute(this.info.name, this.info.surname, this.info.email, this.info.phoneNumber ?? null)
        .pipe(
          map(customer => Success(customer)),
          catchError(error => of(Failure<Customer>(error))),
          startWith(Loading<Customer>())
        )
        .subscribe(
          result => {
            if (result.isLoading) {
              this.view?.showLoading();
            } else {
              this.view?.hideLoading();
              result.fold(
                customer => {
                  Logger.d("customer created, navigating out");
                  this.view?.customerCreated(customer);
                },
                error => {
                  Logger.e(error);
                  this.view?.showErrorDialog();
                }
              );
            }
          },
          Logger.e
        )
    );
  }
};

